import React, { useState, useRef, useEffect } from 'react';
import { Send, Zap, Mic, ArrowLeft, AlertTriangle, TrendingUp, UserPlus, Clock, Users, Briefcase, CalendarDays, Paperclip, Plus, FileText, ArrowRight } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { HIRING_DATA, HIRING_CHAT_RESPONSES } from '../../data/hiringData';

const PRIORITY_STYLES = {
  dark: { high: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/20' }, medium: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/20' }, low: { bg: 'bg-slate-500/10', text: 'text-slate-500', border: 'border-slate-500/20' } },
  light: { high: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' }, medium: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' }, low: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' } },
};

const MetricCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = item.alert ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = item.alert ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : border;
  return (
    <div className={`${cardBg} border ${cardBorder} rounded-xl px-3 py-2.5 text-center`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const RoleCard = ({ role, isDark, onAction }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const ps = (isDark ? PRIORITY_STYLES.dark : PRIORITY_STYLES.light)[role.priority];
  const isBottleneck = role.bottleneck;
  const isHired = role.stage === 'Hired';
  const cardBg = isHired ? (isDark ? 'bg-emerald-950/10' : 'bg-emerald-50/30') : isBottleneck ? (isDark ? 'bg-amber-950/15' : 'bg-amber-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = isHired ? (isDark ? 'border-emerald-500/10' : 'border-emerald-200/40') : isBottleneck ? (isDark ? 'border-amber-500/15' : 'border-amber-200/50') : border;

  return (
    <div className={`${cardBg} border ${cardBorder} rounded-lg px-3 py-2.5 mb-2`} data-testid={`role-card-${role.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${ps.bg} ${ps.text} ${ps.border}`}>{role.priority}</span>
        <p className={`text-[11px] font-semibold flex-1 min-w-0 truncate ${tp}`}>{role.title}</p>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <Briefcase className={`w-3 h-3 ${ts}`} />
        <span className={`text-[9px] ${tp}`}>{role.department}</span>
        <span className={`text-[9px] ${ts}`}>{role.candidates} candidates</span>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <Clock className={`w-3 h-3 ${role.daysOpen > 25 ? (isDark ? 'text-amber-400' : 'text-amber-500') : ts}`} />
        <span className={`text-[9px] font-medium ${role.daysOpen > 25 ? (isDark ? 'text-amber-400' : 'text-amber-500') : ts}`}>{role.daysOpen} days open</span>
      </div>
      <p className={`text-[9px] mt-1.5 ${ts} truncate`}>{role.nextStep}</p>
      <p className={`text-[8px] mt-0.5 ${isDark ? 'text-white/30' : 'text-slate-500'}`}>{role.hiringManager}</p>
      {isBottleneck && (
        <div className="mt-1.5">
          <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-50 text-amber-600'}`}>
            <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />Bottleneck
          </span>
        </div>
      )}
      {/* CTA row */}
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-dashed" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
        {isHired ? (
          <button onClick={() => onAction?.('Start Onboarding', role.title)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium whitespace-nowrap transition-colors ${isDark ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}`}>
            <UserPlus className="w-3 h-3" /> Onboard
          </button>
        ) : isBottleneck ? (
          <button onClick={() => onAction?.('Fast-track Role', role.title)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium whitespace-nowrap transition-colors ${isDark ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'}`}>
            <Zap className="w-3 h-3" /> Fast-track
          </button>
        ) : (
          <button onClick={() => onAction?.('Review Candidates', role.title)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium whitespace-nowrap transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
            <ArrowRight className="w-3 h-3" /> Review
          </button>
        )}
      </div>
    </div>
  );
};

const InsightCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[9px] mt-1 ${ts}`}>Tali · {item.time}</p>
      </div>
    </div>
  );
};

const V4PeopleHiring = ({ isDark, onWorkflowToggle, workflowPanelOpen }) => {
  const [stageFilter, setStageFilter] = useState(null);
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const stages = HIRING_DATA.stages;
  const filteredRoles = stageFilter ? HIRING_DATA.roles.filter(r => r.stage === stageFilter) : HIRING_DATA.roles;

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || HIRING_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };
  const handleCta = (label, detail) => {
    sendMessage(`${label} — ${detail}`, { text: `I'll handle "${label}" for ${detail}. Setting that up now and will confirm once ready.\n\nAnything else you need?` });
  };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Tali about hiring..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="hiring-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.tali} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Tali</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="hiring-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="hiring-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="hiring-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Hiring</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.tali} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  const rolesByStage = {};
  stages.forEach(s => { rolesByStage[s] = filteredRoles.filter(r => r.stage === s); });

  return (
    <div className="flex flex-col h-full" data-testid="hiring-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <UserPlus className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Hiring</p>
            <span className={`text-[10px] ${ts}`}>{filteredRoles.length} roles</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => handleCta('Post New Role', 'Create a new job posting')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="hiring-post-role-btn">
              <Plus className="w-3.5 h-3.5" /> Post New Role
            </button>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {[null, ...stages].map(s => (
            <button key={s || 'all'} onClick={() => setStageFilter(s)}
              className={`px-2 py-1 rounded-lg text-[10px] font-medium border transition-colors ${stageFilter === s ? (isDark ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20') : `${border} ${ts} ${hoverBg}`}`}
              data-testid={`hiring-filter-${(s || 'all').toLowerCase().replace(/\s+/g, '-')}`}
            >{s || 'All'}</button>
          ))}
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
        <div className="grid grid-cols-6 gap-3">
          {HIRING_DATA.metrics.map((m, i) => <MetricCard key={i} item={m} isDark={isDark} />)}
        </div>

        {/* Kanban */}
        <div className="flex gap-3 min-h-0 overflow-x-auto">
          {stages.map(stage => {
            const stageRoles = rolesByStage[stage] || [];
            const isHired = stage === 'Hired';
            const bottleneckCount = stageRoles.filter(r => r.bottleneck).length;
            return (
              <div key={stage} className="w-[220px] flex-shrink-0 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${isHired ? (isDark ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-600') : (isDark ? 'bg-white/[0.04] border-white/[0.08] text-white/70' : 'bg-black/[0.03] border-black/[0.06] text-slate-600')}`}>{stage}</span>
                    <span className={`text-[10px] ${ts}`}>{stageRoles.length}</span>
                  </div>
                  {bottleneckCount > 0 && <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-50 text-amber-600'}`}>{bottleneckCount} stuck</span>}
                </div>
                <div className="flex-1 overflow-y-auto">
                  {stageRoles.map(r => <RoleCard key={r.id} role={r} isDark={isDark} onAction={handleCta} />)}
                  {stageRoles.length === 0 && <p className={`text-[10px] ${ts} text-center py-4`}>No roles</p>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Upcoming interviews */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-xs font-semibold ${tp}`}>Upcoming Interviews</p>
            <span className={`text-[9px] ${ts}`}>{HIRING_DATA.upcomingInterviews.length} this week</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {HIRING_DATA.upcomingInterviews.map((iv, i) => (
              <div key={i} className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} border ${border} rounded-lg px-3 py-2`}>
                <p className={`text-[11px] font-medium ${tp}`}>{iv.candidate}</p>
                <p className={`text-[9px] ${ts}`}>{iv.role}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className={`text-[9px] font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>{iv.time}</span>
                  <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/[0.04] text-white/40' : 'bg-black/[0.03] text-slate-500'}`}>{iv.type}</span>
                </div>
                <div className="mt-1.5 pt-1.5 border-t border-dashed" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                  <button onClick={() => handleCta('Prep Interview Notes', `${iv.candidate} for ${iv.role}`)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
                    <FileText className="w-3 h-3" /> Prep Notes
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Insights */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src={AGENT_THUMBS.tali} alt="" className="w-4 h-4 rounded-full object-cover" />
            <p className={`text-xs font-semibold ${tp}`}>Tali's Insights</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {HIRING_DATA.insights.map(item => <InsightCard key={item.id} item={item} isDark={isDark} />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4PeopleHiring;
